import { $createCodeNode } from '@lexical/code';
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  HeadingTagType,
} from '@lexical/rich-text';
import { $patchStyleText, $setBlocksType } from '@lexical/selection';
import { $isTableSelection } from '@lexical/table';
import { $findMatchingParent, $getNearestBlockElementAncestorOrThrow } from '@lexical/utils';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  LexicalEditor,
  LexicalNode,
} from 'lexical';

import {
  DEFAULT_FONT_SIZE,
  MAX_ALLOWED_FONT_SIZE,
  MIN_ALLOWED_FONT_SIZE,
} from '@/RichTextEditor/constants/toolbar';

export enum UpdateFontSizeType {
  increment = 1,
  decrement,
}

export enum BLOCK_TYPE {
  PARAGRAPH = 'paragraph',
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  BULLET = 'bullet',
  NUMBER = 'number',
  CHECK = 'check',
  QUOTE = 'quote',
  CODE = 'code',
}

export type BLOCK_TYPE_VALUE =
  | 'number'
  | 'paragraph'
  | 'bullet'
  | 'check'
  | 'code'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'quote';

/**
 * Calculates the new font size based on the update type.
 * @param currentFontSize - The current font size
 * @param updateType - The type of change, either increment or decrement
 * @returns the next font size
 */
export const calculateNextFontSize = (
  currentFontSize: number,
  updateType: UpdateFontSizeType | null
) => {
  if (!updateType) {
    return currentFontSize;
  }

  let updatedFontSize: number = currentFontSize;
  switch (updateType) {
    case UpdateFontSizeType.decrement: {
      switch (true) {
        case currentFontSize > MAX_ALLOWED_FONT_SIZE: {
          updatedFontSize = MAX_ALLOWED_FONT_SIZE;
          break;
        }
        case currentFontSize >= 48: {
          updatedFontSize -= 12;
          break;
        }
        case currentFontSize >= 24: {
          updatedFontSize -= 4;
          break;
        }
        case currentFontSize >= 14: {
          updatedFontSize -= 2;
          break;
        }
        case currentFontSize >= 9: {
          updatedFontSize -= 1;
          break;
        }
        default: {
          updatedFontSize = MIN_ALLOWED_FONT_SIZE;
          break;
        }
      }
      break;
    }

    case UpdateFontSizeType.increment: {
      switch (true) {
        case currentFontSize < MIN_ALLOWED_FONT_SIZE: {
          updatedFontSize = MIN_ALLOWED_FONT_SIZE;
          break;
        }
        case currentFontSize < 12: {
          updatedFontSize += 1;
          break;
        }
        case currentFontSize < 20: {
          updatedFontSize += 2;
          break;
        }
        case currentFontSize < 36: {
          updatedFontSize += 4;
          break;
        }
        case currentFontSize <= 60: {
          updatedFontSize += 12;
          break;
        }
        default: {
          updatedFontSize = MAX_ALLOWED_FONT_SIZE;
          break;
        }
      }
      break;
    }

    default: {
      break;
    }
  }
  return updatedFontSize;
};

/**
 * Patches the selection with the updated font size.
 */
export const updateFontSizeInSelection = (
  editor: LexicalEditor,
  newFontSize: string | null,
  updateType: UpdateFontSizeType | null
) => {
  const getNextFontSize = (prevFontSize: string | null): string => {
    if (!prevFontSize) {
      prevFontSize = `${DEFAULT_FONT_SIZE}px`;
    }
    prevFontSize = prevFontSize.slice(0, -2);
    const nextFontSize = calculateNextFontSize(Number(prevFontSize), updateType);
    return `${nextFontSize}px`;
  };

  editor.update(() => {
    if (editor.isEditable()) {
      const selection = $getSelection();
      if (selection !== null) {
        $patchStyleText(selection, {
          'font-size': newFontSize || getNextFontSize,
        });
      }
    }
  });
};

export const updateFontSize = (
  editor: LexicalEditor,
  updateType: UpdateFontSizeType,
  inputValue: number
) => {
  if (inputValue) {
    const nextFontSize = calculateNextFontSize(inputValue, updateType);
    updateFontSizeInSelection(editor, String(nextFontSize) + 'px', null);
  } else {
    updateFontSizeInSelection(editor, null, updateType);
  }
};

export const formatParagraph = (editor: LexicalEditor) => {
  editor.update(() => {
    const selection = $getSelection();
    $setBlocksType(selection, () => $createParagraphNode());
  });
};

export const formatHeading = (
  editor: LexicalEditor,
  blockType: BLOCK_TYPE_VALUE,
  headingSize: HeadingTagType
) => {
  if (blockType !== headingSize) {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createHeadingNode(headingSize));
    });
  }
};

export const formatBulletList = (editor: LexicalEditor, blockType: BLOCK_TYPE_VALUE) => {
  if (blockType === 'bullet') {
    formatParagraph(editor);
  } else {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, void 0);
  }
};

export const formatCheckList = (editor: LexicalEditor, blockType: BLOCK_TYPE_VALUE) => {
  if (blockType === 'check') {
    formatParagraph(editor);
  } else {
    editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, void 0);
  }
};

export const formatNumberedList = (editor: LexicalEditor, blockType: string) => {
  if (blockType === 'number') {
    formatParagraph(editor);
  } else {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, void 0);
  }
};

export const formatQuote = (editor: LexicalEditor, blockType: BLOCK_TYPE_VALUE) => {
  if (blockType !== 'quote') {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createQuoteNode());
    });
  }
};

export const formatCode = (editor: LexicalEditor, blockType: BLOCK_TYPE_VALUE) => {
  if (blockType !== 'code') {
    editor.update(() => {
      let selection = $getSelection();
      if (!selection) {
        return;
      }
      if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        $setBlocksType(selection, () => $createCodeNode());
      } else {
        const textContent = selection.getTextContent();
        const codeNode = $createCodeNode();
        selection.insertNodes([codeNode]);
        selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertRawText(textContent);
        }
      }
    });
  }
};

export const clearFormatting = (editor: LexicalEditor) => {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const anchor = selection.anchor;
      const focus = selection.focus;
      const nodes = selection.getNodes();
      const extractedNodes = selection.extract();

      if (anchor.key === focus.key && anchor.offset === focus.offset) {
        return;
      }

      for (let [idx, node] of nodes.entries()) {
        // We split the first and last node by the selection
        // So that we don't format unselected text inside those nodes
        if ($isTextNode(node)) {
          // Use a separate variable to ensure TS does not lose the refinement
          let textNode = node;
          if (idx === 0 && anchor.offset !== 0) {
            textNode = textNode.splitText(anchor.offset)[1] || textNode;
          }
          if (idx === nodes.length - 1) {
            textNode = textNode.splitText(focus.offset)[0] || textNode;
          }
          /**
           * If the selected text has one format applied
           * selecting a portion of the text, could
           * clear the format to the wrong portion of the text.
           *
           * The cleared text is based on the length of the selected text.
           */
          // We need this in case the selected text only has one format
          const extractedTextNode = extractedNodes[0];
          if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
            textNode = extractedTextNode;
          }

          if (textNode.__style !== '') {
            textNode.setStyle('');
          }
          if (textNode.__format !== 0) {
            textNode.setFormat(0);
          }
          const nearestBlockElement = $getNearestBlockElementAncestorOrThrow(textNode);
          if (nearestBlockElement.__format !== 0) {
            nearestBlockElement.setFormat('');
          }
          if (nearestBlockElement.__indent !== 0) {
            nearestBlockElement.setIndent(0);
          }
          node = textNode;
        } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
          node.replace($createParagraphNode(), true);
        } else if ($isDecoratorBlockNode(node)) {
          node.setFormat('');
        }
      }
    }
  });
};

export function $findTopLevelElement(node: LexicalNode) {
  let topLevelElement =
    node.getKey() === 'root'
      ? node
      : $findMatchingParent(node, e => {
          const parent = e.getParent();
          return parent !== null && $isRootOrShadowRoot(parent);
        });

  if (topLevelElement === null) {
    topLevelElement = node.getTopLevelElementOrThrow();
  }
  return topLevelElement;
}
